import { type ScheduleUploadErrorCode } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import { type ScheduledSessionDraft } from './types';

// Modal states
export const isUploadSessionScheduleModalOpenAtom = atom<boolean>(false);
export const isUploadScheduleUnsavedAlertDialogOpenAtom = atom<boolean>(false);

export const showPreviewAtom = atom<boolean>(false);
export const alertDialogActionAtom = atom<'close' | 'back' | null>(null);

/**
 * Whether the clinician has opted in to extracting per-session context
 * (reason for visit / pre-session notes) from the uploaded schedule.
 *
 * Defaults off: when off we neither surface the context fields in the preview
 * nor forward `context` to bulk session creation. Reset to off at the start of
 * each new upload so "default off" holds for every review. Only takes effect
 * when `useIsScheduleUploadContextFieldEnabled()` (the backend-gated flag) is on.
 */
export const isContextExtractionEnabledAtom = atom<boolean>(false);

/**
 * Whether the clinician has opted in to run the ambient-copilot prechart flow
 * after importing sessions from a `prechart_schedule` handoff.
 *
 * Defaults off: import alone completes without queuing EHR extraction. Reset
 * to off at the start of each new upload / after bulk create / on discard so
 * every review starts opted out. Only surfaced when ambient copilot is on and
 * the preview drafts match an active copilot prechart request.
 */
export const isPrechartingEnabledAtom = atom<boolean>(false);

export const scheduledSessionsDraftAtom = atom<ScheduledSessionDraft[]>([]);

/**
 * True while a schedule-import bulk create is in flight. Shared so the modal,
 * unsaved-alert, and footer all see the same signal — TanStack v5 `isPending`
 * is observer-local and a second `useBulkSessionCreation()` would read false.
 */
export const isBulkCreatingSessionsAtom = atom(false);

/**
 * Clinician the current upload is created for (assistant picker). `undefined` means self when the
 * user can self-create, otherwise no target. Set by the modal's clinician field; reset on a fresh
 * modal open, on batch completion, and on discard — but preserved across a failed-import retry.
 */
export const uploadClinicianMemberIdAtom = atom<string | undefined>(undefined);

/**
 * Timestamp (Date.now()) set when the copilot AX-assist begins validating the
 * schedule after the preview opened on deterministic drafts. Import stays
 * disabled while non-null and within SCHEDULE_ASSIST_VALIDATION_TTL_MS. Null
 * once validation resolves or the TTL elapses so the import is never blocked
 * forever when the follow-up intent does not arrive.
 */
export const SCHEDULE_ASSIST_VALIDATION_TTL_MS = 30_000;
export const scheduleAssistValidationPendingAtom = atom<number | null>(null);

/**
 * One-shot signal that a schedule upload just bulk-created sessions for the
 * current user, carrying the created sessions' local datetimes. The session
 * list consumes (and clears) it: scroll to top, and expand the collapsed
 * Upcoming section when any created session is future-dated, so the user can
 * scan that the upload landed as expected. Uploads for another clinician never
 * set it — those sessions appear in the target clinician's list, not this one.
 */
export type ScheduleUploadRevealSignal = {
  createdTimesLocal: string[];
};

export const scheduleUploadRevealSignalAtom = atom<ScheduleUploadRevealSignal | null>(
  null
);

export type UploadErrors = {
  failures: ScheduleUploadErrorCode[];
  warnings: ScheduleUploadErrorCode[];
  failureCount: number;
  warningCount: number;
};

const initialUploadErrors: UploadErrors = {
  failures: [],
  warnings: [],
  failureCount: 0,
  warningCount: 0,
};

export const uploadErrorsAtom = atom<UploadErrors>(initialUploadErrors);
